import { AdFarmApiClient } from "./AdFarmApiClient";
import { DeviceRiskPayload } from "./AdFarmTypes";
import { SecureStorage } from "./SecureStorage";
import { AdFarmConfig } from "./AdFarmConfig";

type NativeRiskBridge = {
    getDeviceId(): string | Promise<string>;
    provider?: string;
    sdkVersion?: string;
};

export class RiskService {
    private static readonly CONSENT_KEY = "privacy-consent";
    private static readonly DEVICE_KEY = "device-id";
    private consentAccepted = false;
    private payload: DeviceRiskPayload | null = null;

    constructor(private readonly api: AdFarmApiClient) {}

    async restoreConsent(): Promise<boolean> {
        if (AdFarmConfig.RISK_DISABLED) {
            this.consentAccepted = true;
            this.payload = this.disabledPayload();
            return true;
        }
        this.consentAccepted = (await SecureStorage.get(RiskService.CONSENT_KEY)) === "accepted";
        if (this.consentAccepted) await this.initializeAfterConsent();
        return this.consentAccepted;
    }

    async acceptPrivacy(): Promise<DeviceRiskPayload> {
        if (AdFarmConfig.RISK_DISABLED) {
            this.consentAccepted = true;
            this.payload = this.disabledPayload();
            return this.payload;
        }
        this.consentAccepted = true;
        await SecureStorage.set(RiskService.CONSENT_KEY, "accepted");
        return this.initializeAfterConsent();
    }

    hasConsent(): boolean {
        return this.consentAccepted;
    }

    async getPayload(): Promise<DeviceRiskPayload> {
        if (AdFarmConfig.RISK_DISABLED) return this.payload || this.disabledPayload();
        if (!this.consentAccepted) throw new Error("请先阅读并同意隐私政策与用户协议");
        return this.payload || this.initializeAfterConsent();
    }

    async checkBeforeAd(): Promise<unknown> {
        if (AdFarmConfig.RISK_DISABLED) return { allowed: true, mode: "risk-disabled" };
        return this.api.checkAdRisk(await this.getPayload());
    }

    private disabledPayload(): DeviceRiskPayload {
        return {
            deviceId: "local-risk-disabled",
            deviceRiskProvider: "disabled",
            deviceRiskSdkVersion: "local-test",
            privacyConsentAccepted: true,
        };
    }

    private nativeBridge(): NativeRiskBridge | null {
        const host = globalThis as unknown as { AdFarmNativeBridge?: { risk?: NativeRiskBridge } };
        return host.AdFarmNativeBridge?.risk || null;
    }

    private async initializeAfterConsent(): Promise<DeviceRiskPayload> {
        const bridge = this.nativeBridge();
        let deviceId = "";
        let provider = "web-demo";
        let sdkVersion = "not-configured";
        if (bridge) {
            deviceId = (await bridge.getDeviceId()) || "";
            provider = bridge.provider || "shumei";
            sdkVersion = bridge.sdkVersion || "unknown";
        }
        if (!deviceId) deviceId = await SecureStorage.get(RiskService.DEVICE_KEY);
        if (!deviceId) {
            const nonce = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
            deviceId = `laya-${nonce}`;
            await SecureStorage.set(RiskService.DEVICE_KEY, deviceId);
        }
        this.payload = {
            deviceId,
            deviceRiskProvider: provider,
            deviceRiskSdkVersion: sdkVersion,
            privacyConsentAccepted: true,
        };
        return this.payload;
    }
}
